import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import closeIntelApi from './closeIntelApi';
import SectionGuide from './SectionGuide';

const fmtNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString() : '0';
};

const verdictColor = (verdict) => {
  if (verdict === 'real') return 'var(--ace-positive)';
  if (verdict === 'noise') return 'var(--ace-negative)';
  return '#FFAC03';
};

function CountChain({ counts = {} }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
      {[
        ['Candidates', counts.nCandidates],
        ['Survived', counts.nSurvived],
        ['Suppressed', counts.nSuppressed],
        ['Findings', counts.nFindings]
      ].map(([label, value]) => (
        <div
          key={label}
          style={{
            border: `1px solid ${label === 'Suppressed' ? '#FFAC03' : 'var(--ace-line)'}`,
            borderRadius: 14,
            padding: 12,
            background: 'transparent'
          }}
        >
          <div className="ace-eyebrow">{label}</div>
          <strong style={{ fontSize: label === 'Suppressed' ? 30 : 22, color: label === 'Suppressed' ? '#FFAC03' : 'inherit' }}>
            {fmtNumber(value)}
          </strong>
        </div>
      ))}
    </div>
  );
}

export default function AgentMemoryPanel() {
  const [memory, setMemory] = useState(null);
  const [busyRule, setBusyRule] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    setError('');
    try {
      setMemory(await closeIntelApi.agentMemory());
    } catch (e) {
      setError(String(e.message || e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleRule = async (rule) => {
    setBusyRule(rule.id);
    setError('');
    try {
      const updated = await closeIntelApi.saveRule({ ...rule, active: !rule.active });
      setMemory((current) => ({
        ...current,
        rules: (current?.rules || []).map((item) => (item.id === rule.id ? { ...item, ...updated } : item))
      }));
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusyRule('');
    }
  };

  const rules = memory?.rules || [];
  const dispositions = memory?.recentDispositions || [];

  return (
    <section className="ace-panel" style={{ padding: 18, borderRadius: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
        <div>
          <div className="ace-eyebrow">CI_02 <span aria-hidden>●</span> Agent memory</div>
          <h2 style={{ margin: '5px 0 0' }}>Disposition to rule to suppression</h2>
          <SectionGuide guideId="memory" />
        </div>
        <button type="button" className="ace-pill" onClick={load}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {error && <p style={{ color: 'var(--ace-negative)' }}>{error}</p>}

      <div style={{ display: 'grid', gap: 16 }}>
        <section style={{ border: '1px solid var(--ace-line)', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: 12, borderBottom: '1px solid var(--ace-line)' }}>
            <div className="ace-eyebrow">Active rules</div>
          </div>
          {rules.map((rule) => (
            <div key={rule.id} style={{ display: 'grid', gridTemplateColumns: '88px minmax(0, 1fr)', gap: 12, padding: 12, borderBottom: '1px solid var(--ace-line)' }}>
              <button
                type="button"
                className="ace-pill"
                disabled={busyRule === rule.id}
                onClick={() => toggleRule(rule)}
                style={{ color: rule.active ? 'var(--ace-positive)' : 'hsl(var(--muted-foreground))' }}
              >
                {rule.active ? 'Active' : 'Paused'}
              </button>
              <div>
                <strong>{rule.planningEntity || 'All entities'} · {rule.glGroup || rule.glAccount || 'All GL'}</strong>
                <small style={{ display: 'block', color: 'hsl(var(--muted-foreground))' }}>
                  {rule.recurrence || 'recurrence n/a'} · month {rule.monthOfYear || 'any'} · action {rule.action || 'suppress'}
                </small>
                <p style={{ margin: '6px 0 0' }}>{rule.sourceDispositionNote || rule.note || 'No source note provided.'}</p>
              </div>
            </div>
          ))}
          {!rules.length && <p style={{ margin: 0, padding: 12, color: 'hsl(var(--muted-foreground))' }}>No memory rules returned.</p>}
        </section>

        <section style={{ border: '1px solid var(--ace-line)', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: 12, borderBottom: '1px solid var(--ace-line)' }}>
            <div className="ace-eyebrow">Recent dispositions</div>
          </div>
          {dispositions.map((disp) => (
            <div key={disp.id || `${disp.findingId}-${disp.createdAt}`} style={{ display: 'grid', gridTemplateColumns: '150px minmax(0, 1fr)', gap: 12, padding: 12, borderBottom: '1px solid var(--ace-line)' }}>
              <span className="ace-pill" style={{ color: verdictColor(disp.verdict), justifySelf: 'start' }}>
                {disp.verdict || 'verdict'}
              </span>
              <div>
                <strong>{disp.planningEntity || disp.finding?.planningEntity || 'Company'} · {disp.glGroup || disp.finding?.glGroup || 'Finding scope'}</strong>
                <p style={{ margin: '5px 0 0', color: 'hsl(var(--muted-foreground))' }}>{disp.note || disp.sourceDispositionNote || 'No note.'}</p>
              </div>
            </div>
          ))}
          {!dispositions.length && <p style={{ margin: 0, padding: 12, color: 'hsl(var(--muted-foreground))' }}>No dispositions returned.</p>}
        </section>

        <section style={{ border: '1px solid var(--ace-line)', borderRadius: 14, padding: 12 }}>
          <div className="ace-eyebrow" style={{ marginBottom: 10 }}>Last run</div>
          <CountChain counts={memory?.lastRun?.counts || memory?.lastRun || {}} />
        </section>
      </div>
    </section>
  );
}
